import { useMemo } from "react";
import { useAppSelector } from "@/stores/hook";
import {
  DAY_END_24,
  DAY_START_24,
  isWholeDay,
} from "../components/leadFilter/components/DateFilters/timeOfDay";
import { ALL_IDLE_TOUCH_KEYS } from "../components/leadFilter/components/Activityfilters/idleTouches";

/** `"12"` / `"12,40"` — a comma list of row ids (the ad set / ad filters). */
const ID_LIST = /^\d+(,\d+)*$/;
const isIdList = (v: unknown): v is string =>
  typeof v === "string" && ID_LIST.test(v);

/** `"2026-0,2026-8"` — the report bar's intake keys (month 0-indexed). */
const INTAKE_KEYS = /^\d{4}-\d{1,2}(,\d{4}-\d{1,2})*$/;
export const isIntakeKeys = (v: unknown): v is string =>
  typeof v === "string" && INTAKE_KEYS.test(v);

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
/** `"2026-0,2026-8"` → `"Jan 2026, Sep 2026"` — the Intake chip's text. */
export function intakeKeysLabel(keys: string): string {
  return keys
    .split(",")
    .map((k) => {
      const [y, m0] = k.split("-");
      const name = MONTHS_SHORT[Number(m0)];
      return name ? `${name} ${y}` : k;
    })
    .join(", ");
}

/**
 * Normalizes appliedFilters into clean query params.
 * Shared by both lead and dynamic filter hooks.
 */
export function serializeFilterParams(
  appliedFilters: Record<string, any>,
  search?: string
): Record<string, any> {
  const params: Record<string, any> = { ...appliedFilters };
  if (search) params.search = search;

  // hasChoices toggle → backend param `hasChoice`
  if (params.hasChoices === true) {
    params.hasChoice = true;
  }
  delete params.hasChoices;

  // isRevoked toggle → backend param `isRevoked` (string "true" is reliably parsed by Express/qs)
  if (
    params.isRevoked === true ||
    params.isRevoked === "true" ||
    params.isRevoked === 1
  ) {
    params.isRevoked = "true";
  } else {
    delete params.isRevoked;
  }

  // revokeType sub-bucket — only meaningful with isRevoked=true; drop
  // otherwise so the URL doesn't carry an inert "all" parameter.
  if (
    params.isRevoked === "true" &&
    typeof params.revokeType === "string" &&
    params.revokeType !== "all"
  ) {
    params.revokeType = params.revokeType;
  } else {
    delete params.revokeType;
  }

  // isReenquired toggle → backend param `isReenquired`:
  //   "true" = the leads still carrying the re-enquiry badge (the pill's default)
  //   "all"  = every lead that ever re-enquired (the "All re-enquired · N" button)
  if (params.isReenquired === "all") {
    params.isReenquired = "all";
  } else if (
    params.isReenquired === true ||
    params.isReenquired === "true" ||
    params.isReenquired === 1
  ) {
    params.isReenquired = "true";
  } else {
    delete params.isReenquired;
  }

  // isActive toggle → only send when explicitly Active or Inactive
  if (params.isActive === "Active") {
    params.isActive = true;
  } else if (params.isActive === "Inactive") {
    params.isActive = false;
  } else {
    delete params.isActive;
  }

  // Anchor ("Verified") filter (old-CRM "uniquify" port) → backend param
  // `verified=<anchorStageId>` (the selected anchor's stage id). The conditions +
  // button name live on that isAnchorPoint stage and are read server-side, so
  // only the stage id is sent. Absent/null = off.
  if (typeof params.verifiedAnchor === "number" && params.verifiedAnchor > 0) {
    params.verified = String(params.verifiedAnchor);
  }
  delete params.verifiedAnchor;

  // hasActiveTrack toggle
  if (params.hasActiveTrack === true) {
    params.hasActiveTrack = true;
  } else {
    delete params.hasActiveTrack;
  }

  // Admin-only no-branch filter. When active, it owns branch filtering.
  if (
    params.noBranch === true ||
    params.noBranch === "true" ||
    params.noBranch === 1
  ) {
    params.noBranch = "true";
    delete params.branchId;
  } else {
    delete params.noBranch;
  }

  // Payment status: drop when nothing or *everything* is selected (every child
  // always has one of the 4 canonical statuses, so "all selected" is a no-op
  // intent — sending it would still exclude leads with zero children because
  // of the `child.some` shape on the server).
  const PAYMENT_STATUS_VALUES = ["pending", "advance", "partial", "full"];
  if (typeof params.paymentStatus === "string") {
    const selected = params.paymentStatus
      .split(",")
      .map((s: string) => s.trim().toLowerCase())
      .filter(Boolean);
    if (
      selected.length === 0 ||
      selected.length === PAYMENT_STATUS_VALUES.length
    ) {
      delete params.paymentStatus;
    } else {
      params.paymentStatus = selected.join(",");
    }
  }

  // Payment toggles (apply to children)
  if (params.hasDealValue === true) {
    params.hasDealValue = true;
  } else {
    delete params.hasDealValue;
  }
  if (params.hasInstallmentPlan === true) {
    params.hasInstallmentPlan = true;
  } else {
    delete params.hasInstallmentPlan;
  }

  if (params.hasOverdueInstallments === true) {
    params.hasOverdueInstallments = true;
  } else {
    delete params.hasOverdueInstallments;
  }

  if (params.hasPendingAmount === true) {
    params.hasPendingAmount = true;
  } else {
    delete params.hasPendingAmount;
  }

  // Instalment rules / next-due window: send only when set.
  for (const k of ["instRules", "instDue"] as const) {
    if (typeof params[k] !== "string" || params[k].trim() === "") delete params[k];
  }

  // Payment numeric ranges: strip when 0 / empty
  for (const k of [
    "minDealValue",
    "maxDealValue",
    "minPendingAmount",
    "maxPendingAmount",
  ] as const) {
    const v = params[k];
    if (v == null || v === "" || Number(v) === 0) delete params[k];
  }

  // Status-Changed-At → entered a SPECIFIC stage. Only meaningful with that
  // date type; sent as `stageChangedToStageId`. Null/absent = "Any stage change"
  // (the blanket last-stageChange window the server already applies).
  if (
    params.dateType === "statusChangedAt" &&
    typeof params.stageChangedTo === "string" &&
    params.stageChangedTo.trim() !== ""
  ) {
    // Comma list — the server matches it as `stageId IN (…)`, i.e. "entered
    // ANY of these stages in the window".
    params.stageChangedToStageId = params.stageChangedTo
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean)
      .join(",");
  }
  delete params.stageChangedTo;

  // "Even if it has moved on" (FilterState.entryMovedOn). It widens an ENTRY
  // reading, so it only means anything with one armed: the Status-Changed-At
  // clock and a stage target, or a status / sub-status level with the chips
  // it points at (owner, 2026-10-09: "how many Verification Failed happened
  // in a range, even if it moved to Potential"). Anywhere else it would be an
  // inert marker riding into the server's cache key.
  const movedOnLevel = String(params.entryLevel ?? "");
  const movedOnHasStatus = String(params.status ?? "").trim() !== "";
  const movedOnHasSub = String(params.subStatus ?? "").trim() !== "";
  if (
    params.entryMovedOn === true &&
    params.dateType === "statusChangedAt" &&
    (String(params.stageChangedToStageId ?? "").trim() !== "" ||
      (movedOnLevel === "status" && movedOnHasStatus) ||
      (movedOnLevel === "subStatus" && (movedOnHasSub || movedOnHasStatus)))
  ) {
    params.entryMovedOn = "true";
  } else {
    delete params.entryMovedOn;
  }

  // Status-Changed-At carries the intake-stage rule: the first stage's id
  // rides along so the server can put that stage's bucket on createdAt
  // (leads are created into it). Meaningless under any other date type.
  if (
    params.dateType !== "statusChangedAt" ||
    !String(params.intakeStageId ?? "").trim()
  ) {
    delete params.intakeStageId;
  }

  // Which level's change "Status Changed At" counts (FilterState.entryLevel).
  // Only meaningful under that date type AND with the ids it points at — a
  // level with no matching chips would be a clock on nothing, and the server
  // would ignore it anyway; keep the query byte-identical to the stage-level
  // reading instead of carrying an inert marker into its cache key.
  {
    const level = String(params.entryLevel ?? "");
    const hasStatus = String(params.status ?? "").trim() !== "";
    const hasSub = String(params.subStatus ?? "").trim() !== "";
    // "stage" is only worth sending where the server would otherwise default
    // AWAY from it: status chips under an armed entry target.
    const hasTarget = String(params.stageChangedToStageId ?? "").trim() !== "";
    const keep =
      params.dateType === "statusChangedAt" &&
      ((level === "status" && hasStatus) ||
        (level === "subStatus" && hasSub) ||
        (level === "stage" && hasStatus && hasTarget));
    if (!keep) delete params.entryLevel;
  }

  // Report-drill stage grain — only meaningful while a stage/status/sub-status
  // filter is actually applied.
  //   lead        — bucket by the lead's OWN stage row
  //   matchedDeal — bucket by the lead's representative deal that carries the
  //                 active deal-grain custom field (Intake / Country / cf_*);
  //                 the server falls back to `lead` when there is no such
  //                 filter, which is exactly what the matrices do too.
  if (
    !["lead", "matchedDeal"].includes(String(params.stageGrain ?? "")) ||
    !(params.stage || params.status || params.subStatus)
  ) {
    delete params.stageGrain;
  }

  // Report-drill campaign grain — only meaningful while a campaign filter is
  // applied. "lead" = the lead's OWN campaign (the campaign report's rows);
  // absent = the page default, which also lists re-enquiries through it.
  if (params.campaignGrain !== "lead" || !params.campaignId) {
    delete params.campaignGrain;
  }

  // Meta ad set / ad (drawer pickers + report drill ↗s): comma lists of row
  // ids. Anything else is dropped rather than sent — the server would ignore it.
  for (const k of ["adsetId", "adId"] as const) {
    if (!isIdList(params[k])) delete params[k];
  }

  // The report bar's Intake pill (report drill ↗s): `<year>-<month0>` keys.
  // Anything else is dropped rather than sent — the server would ignore it,
  // and an inert param only fragments its cache key.
  if (!isIntakeKeys(params.intakeKeys)) {
    delete params.intakeKeys;
  }

  // Archived policy (report drill ↗s): only "all" is ever sent — absence IS
  // the page default (archived hidden), and "false" would just restate it.
  if (params.archived !== "all") {
    delete params.archived;
  }

  // Lead-status outcome (report drill ↗s): the reports' Active/Inactive rule
  // (the wire values stay ongoing/rejected).
  // Only the two known values ride; anything else is no narrowing at all.
  if (params.leadOutcome !== "ongoing" && params.leadOutcome !== "rejected") {
    delete params.leadOutcome;
  }

  // Only strip dateType when not explicitly set
  if (!params.dateType) {
    delete params.dateType;
  }

  // Date range: always send both or neither (backend + UI assume a pair)
  const sd = params.startDate;
  const ed = params.endDate;
  const hasStart = sd != null && String(sd).trim() !== "";
  const hasEnd = ed != null && String(ed).trim() !== "";
  if (hasStart !== hasEnd) {
    delete params.startDate;
    delete params.endDate;
  }

  // Time-of-day narrowing (IST "HH:mm"). Only meaningful alongside a date
  // range, and only worth sending when it actually narrows something — a
  // 12:00 AM–11:59 PM pair is the whole day, so it stays off the URL rather
  // than showing up as an "active" filter that changes nothing.
  const rangeSurvived =
    params.startDate != null &&
    String(params.startDate).trim() !== "" &&
    params.endDate != null &&
    String(params.endDate).trim() !== "";
  if (!rangeSurvived || isWholeDay(params.startTime, params.endTime)) {
    delete params.startTime;
    delete params.endTime;
  } else {
    // Send both sides so the server never has to guess the open end.
    if (!String(params.startTime ?? "").trim()) params.startTime = DAY_START_24;
    if (!String(params.endTime ?? "").trim()) params.endTime = DAY_END_24;
  }

  // Activity filters — strip empties so the URL doesn't carry inert keys.
  // The defaults from FilterContext are 0 / "" / "" / "", which the backend
  // would otherwise see as "filter requested but empty" and might apply.
  if (
    params.inactiveFor == null ||
    params.inactiveFor === "" ||
    Number(params.inactiveFor) === 0
  ) {
    delete params.inactiveFor;
  }
  // Aligned idle filter (report-drill target) — same strip rule as inactiveFor.
  if (
    params.idleOpenFor == null ||
    params.idleOpenFor === "" ||
    Number(params.idleOpenFor) === 0
  ) {
    delete params.idleOpenFor;
  }
  // Which activity kinds `idleOpenFor` counts (updates|followups|notes|calls).
  // Inert without the days threshold, and "" / the full set both mean the
  // server default (ALL kinds — the report widget's definition), so only a
  // real subset is sent.
  {
    const touches = String(params.idleTouches ?? "")
      .split(",")
      .map((t: string) => t.trim())
      .filter(Boolean);
    if (
      params.idleOpenFor == null ||
      touches.length === 0 ||
      touches.length >= ALL_IDLE_TOUCH_KEYS.length
    ) {
      delete params.idleTouches;
    } else {
      params.idleTouches = touches.join(",");
    }
  }
  for (const k of [
    "lastContactedStartDate",
    "lastContactedEndDate",
    "contactedBy",
  ] as const) {
    const v = params[k];
    if (v == null || String(v).trim() === "") delete params[k];
  }
  // Last-contacted range: same pair-or-nothing rule as the main date range.
  const lcS = params.lastContactedStartDate;
  const lcE = params.lastContactedEndDate;
  const hasLcS = lcS != null && String(lcS).trim() !== "";
  const hasLcE = lcE != null && String(lcE).trim() !== "";
  if (hasLcS !== hasLcE) {
    delete params.lastContactedStartDate;
    delete params.lastContactedEndDate;
  }

  // Call filters — strip everything inert so the URL (and the applied-chip
  // row) only carries what the user actually asked for.
  for (const k of [
    "callActivity",
    "callDirection",
    "callOutcome",
    "callCategory",
    "callBy",
    // Target filter: a line id, whose progress (empty = the viewer), as of.
    "targetLineId",
    "targetUserId",
    "targetAsOf",
  ] as const) {
    const v = params[k];
    if (v == null || String(v).trim() === "") delete params[k];
  }
  // Whose / as-of mean nothing without a line.
  if (!params.targetLineId) {
    delete params.targetUserId;
    delete params.targetAsOf;
  }
  for (const k of [
    "minCallDuration",
    "maxCallDuration",
    "minCallCount",
    "maxCallCount",
  ] as const) {
    const v = params[k];
    if (v == null || v === "" || Number(v) === 0) delete params[k];
  }
  if (params.hasCallRecording === true) {
    params.hasCallRecording = true;
  } else {
    delete params.hasCallRecording;
  }
  // Call window: ONE picked date is a complete filter — mirror it to the other
  // bound so a single date means THAT day (the server reads each bound as an
  // IST day boundary, so start == end spans the full day). This is what makes
  // "Never called + today" work as "not called today" without forcing the user
  // to click the same day twice. Only a fully empty pair is dropped.
  const callS = params.callStartDate;
  const callE = params.callEndDate;
  const hasCallS = callS != null && String(callS).trim() !== "";
  const hasCallE = callE != null && String(callE).trim() !== "";
  if (hasCallS && !hasCallE) params.callEndDate = callS;
  else if (hasCallE && !hasCallS) params.callStartDate = callE;
  else if (!hasCallS && !hasCallE) {
    delete params.callStartDate;
    delete params.callEndDate;
  }
  // ONE role meaning on the leads page — the twin of the dashboards'
  // `adoptRolePillAsSlotNarrow`: with a follow-up window active, the Role
  // filter means "those roles' follow-ups" — sent as the slot narrow
  // (`followUpRoleId`, a managerial role = responsibility) and STRIPPED from
  // the assignment path so the same pick can't double-filter. No window → the
  // plain assignment filter, exactly as before.
  //
  // SEVERAL roles are now sent as a COMMA LIST (2026-09-09, when the
  // dashboard's follow-up Role pill went multi-select): the server reads them
  // as the UNION of what each means alone. Before, a multi-role pick silently
  // fell back to the assignment filter, so the leads page answered a
  // different question than the dashboard did for the same selection.
  delete params.followUpRoleId;
  if (
    (params.followupStartDate || params.followupEndDate) &&
    typeof params.role === "string"
  ) {
    const roleIds = params.role
      .split(",")
      .map((r) => r.trim())
      .filter((r) => Number(r) > 0);
    if (roleIds.length > 0) {
      params.followUpRoleId = roleIds.join(",");
      delete params.role;
    }
  }

  // Call time-of-day rides only on a call date window (same rule as the main
  // range's startTime/endTime).
  if (!params.callStartDate && !params.callEndDate) {
    delete params.callStartTime;
    delete params.callEndTime;
  } else {
    for (const k of ["callStartTime", "callEndTime"] as const) {
      if (params[k] == null || String(params[k]).trim() === "")
        delete params[k];
    }
  }
  // "Never called" is a relation-level `none`, so a count range on top of it is
  // contradictory (zero calls can't also be "at least 2"). Drop it rather than
  // ship a filter pair that always returns nothing.
  if (params.callActivity === "none") {
    delete params.minCallCount;
    delete params.maxCallCount;
  }

  // Follow-up board strip ↗ (dashboard): the card's request, or nothing.
  if (typeof params.followUpLoad !== "string" || params.followUpLoad.trim() === "") {
    delete params.followUpLoad;
  }

  // Actor-activity filter — drop the whole group when no type is set, so an
  // inert quartet of empty params never rides every request.
  if (!params.activityType || String(params.activityType).trim() === "") {
    delete params.activityType;
    delete params.activityBy;
    delete params.activityStart;
    delete params.activityEnd;
  } else {
    for (const k of ["activityBy", "activityStart", "activityEnd"] as const) {
      if (params[k] == null || String(params[k]).trim() === "") delete params[k];
    }
  }

  // Flatten nicheFilters → top-level params (?universityId=3&courseId=5)
  if (params.nicheFilters) {
    const niche = params.nicheFilters as Record<string, unknown>;
    for (const [k, v] of Object.entries(niche)) {
      if (v !== "" && v != null) params[k] = v;
    }
    delete params.nicheFilters;
  }

  // Flatten customFieldFilters → top-level params (?cf_<id>=<optionId>)
  if (params.customFieldFilters) {
    const cf = params.customFieldFilters as Record<string, string>;
    for (const [fieldId, optionId] of Object.entries(cf)) {
      if (optionId) params[`cf_${fieldId}`] = optionId;
    }
    delete params.customFieldFilters;
  }

  // Flatten customFieldPairs → correlated params
  // (?cfPair_<fieldA>_<fieldB>=<optA>-<optB>,…). Unlike two independent `cf_`
  // filters these match BOTH values on the same deal, so "Dec 2026 + Jan 2027"
  // stays two intakes instead of expanding to four.
  if (params.customFieldPairs) {
    const pairs = params.customFieldPairs as Record<string, string>;
    for (const [bucketKey, tokens] of Object.entries(pairs)) {
      if (tokens) params[`cfPair_${bucketKey}`] = tokens;
    }
    delete params.customFieldPairs;
  }

  return params;
}

/**
 * Combines live search with the snapshot of panel filters
 * that were committed via the "Apply filters" button.
 * Returns a clean params object ready to pass to useLeads().
 */
export function useLeadFilterParams(): Record<string, any> {
  const { search, appliedFilters } = useAppSelector(
    (state) => state.leadFilterSlice
  );

  return useMemo(
    () => serializeFilterParams(appliedFilters, search),
    [search, appliedFilters]
  );
}
